import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Fade,
  useScrollTrigger
} from '@mui/material';
import {
  Business,
  Assignment,
  Home,
  BusinessCenter
} from '@mui/icons-material';
import { apiService } from '@services/api';
import {
  LANDING_CONTAINER_MAX_WIDTH,
  landingGridSpacing,
  landingSectionSpacing,
} from '@/constants/landingLayout';

interface StatsData {
  clients: {
    total: number;
    active: number;
  };
  demands: {
    total: number;
    completed: number;
    completionRate: number;
  };
  units: {
    total: number;
  };
  users: {
    active: number;
  };
  tenants: {
    total: number;
  };
}

interface StatCardProps {
  icon: React.ReactNode;
  value: number;
  label: string;
  suffix?: string | undefined;
  delay?: number | undefined;
}

const StatCard: React.FC<StatCardProps> = ({ icon, value, label, suffix = '', delay = 0 }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger animation when component mounts
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  useEffect(() => {
    if (!isVisible) return;

    const duration = 2000; // 2 seconds
    const steps = 60;
    const increment = value / steps;
    let current = 0;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      current = Math.min(increment * step, value);
      setDisplayValue(Math.floor(current));

      if (step >= steps) {
        setDisplayValue(value);
        clearInterval(timer);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [value, isVisible]);

  return (
    <Fade in={isVisible} timeout={1000}>
      <Box
        sx={{
          textAlign: 'center',
          p: 4,
          borderRadius: 2,
          bgcolor: 'white',
          border: '1px solid',
          borderColor: 'divider',
          transition: 'transform 0.3s, box-shadow 0.3s',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: 6,
            borderColor: 'primary.main'
          }
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            p: 2,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            color: 'white',
            mb: 2
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="h3"
          component="div"
          sx={{
            fontWeight: 'bold',
            color: 'primary.main',
            mb: 1,
            fontSize: { xs: '2rem', md: '3rem' }
          }}
        >
          {displayValue.toLocaleString('pt-BR')}{suffix}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ fontSize: { xs: '0.9rem', md: '1rem' } }}
        >
          {label}
        </Typography>
      </Box>
    </Fade>
  );
};

const StatsSection: React.FC = () => {
  const [stats, setStats] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);
  useScrollTrigger({
    threshold: 100,
    disableHysteresis: true
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Force no cache on first load to get fresh data
        const response = await apiService.getPublicStats(true);
        if (response.data.success) {
          setStats(response.data.data);
        }
      } catch (error: unknown) {
        // Set fallback stats on error
        setStats({
          clients: { total: 0, active: 0 },
          demands: { total: 0, completed: 0, completionRate: 0 },
          units: { total: 0 },
          users: { active: 0 },
          tenants: { total: 0 }
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading || !stats) {
    return null; // Don't show anything while loading
  }

  const statsCards: Array<{
    icon: React.ReactNode;
    value: number;
    label: string;
    suffix?: string;
    delay: number;
  }> = [
    {
      icon: <BusinessCenter sx={{ fontSize: 40 }} />,
      value: stats.tenants.total,
      label: 'Clientes',
      delay: 0
    },
    {
      icon: <Business sx={{ fontSize: 40 }} />,
      value: stats.clients.active,
      label: 'Condomínios',
      delay: 200
    },
    {
      icon: <Assignment sx={{ fontSize: 40 }} />,
      value: stats.demands.total,
      label: 'Demandas',
      delay: 400
    },
    {
      icon: <Home sx={{ fontSize: 40 }} />,
      value: stats.units.total,
      label: 'Unidades',
      delay: 600
    }
  ];

  return (
    <Box
      id="stats"
      sx={{
        ...landingSectionSpacing,
        bgcolor: 'background.paper',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(33, 49, 106, 0.03) 0%, rgba(36, 226, 228, 0.03) 100%)',
          zIndex: 0
        }}
      />
      
      <Container maxWidth={LANDING_CONTAINER_MAX_WIDTH} sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: 'bold',
              mb: 2,
              fontSize: { xs: '1.75rem', md: '2.5rem' }
            }}
          >
            VOVER em Números
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              maxWidth: '600px',
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.1rem' }
            }}
          >
            Confiança e resultados comprovados. Veja o impacto que estamos gerando na gestão de demandas.
          </Typography>
        </Box>

        <Grid container spacing={landingGridSpacing.cards}>
          {statsCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <StatCard
                icon={card.icon}
                value={card.value}
                label={card.label}
                {...(card.suffix !== undefined && { suffix: card.suffix })}
                delay={card.delay}
              />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default StatsSection;

